import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Filter as FilterIcon } from "lucide-react";
import { adminApi } from "../../api/admin.api.js";
import { Pagination } from "../../components/data-table/Pagination.jsx";
import { Badge } from "../../components/ui/Badge.jsx";
import { Spinner } from "../../components/ui/Spinner.jsx";
import { Select } from "../../components/ui/Select.jsx";
import { Input } from "../../components/ui/Input.jsx";
import { EmptyState } from "../../components/ui/EmptyState.jsx";
import { formatDateTime, formatRelativeTime } from "../../lib/format.js";

const ACTION_TONE = {
  create: "success",
  update: "info",
  delete: "danger",
  login: "accent",
  logout: "default",
  upload: "info",
  upload_delete: "default",
};

export default function ActivityLogsPage() {
  const [page, setPage] = useState(1);
  const [action, setAction] = useState("");
  const [resource, setResource] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    document.title = "Activity Logs · Admin";
  }, []);

  const params = {
    page,
    limit: 50,
    ...(action && { action }),
    ...(resource && { resource }),
    ...(fromDate && { fromDate: new Date(fromDate).toISOString() }),
  };

  const { data, isLoading } = useQuery({
    queryKey: ["activity-logs", params],
    queryFn: () => adminApi.list("activity-logs", params),
    staleTime: 15_000,
  });

  useEffect(() => {
    setPage(1);
  }, [action, resource, fromDate]);

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs uppercase tracking-widest text-accent">System</p>
        <h1 className="mt-1 font-display text-2xl text-text-primary">Activity Logs</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Read-only audit trail of every admin write across the system.
        </p>
      </div>

      <div className="overflow-hidden rounded-xl border border-border-subtle">
        <div className="flex flex-wrap items-end gap-3 border-b border-border-subtle bg-bg-elevated p-4">
          <FilterIcon className="h-4 w-4 text-text-muted" />
          <Select
            value={action}
            onChange={(e) => setAction(e.target.value)}
            label="Action"
            className="min-w-[160px]"
          >
            <option value="">Any action</option>
            <option value="create">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
            <option value="login">Login</option>
            <option value="logout">Logout</option>
            <option value="upload">Upload</option>
            <option value="upload_delete">Upload delete</option>
          </Select>
          <Input
            label="Resource"
            placeholder="e.g. designs, blogs, forms"
            value={resource}
            onChange={(e) => setResource(e.target.value)}
            className="min-w-[180px]"
          />
          <Input
            label="From date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="min-w-[160px]"
          />
        </div>

        {isLoading ? (
          <div className="py-16 text-center">
            <Spinner size="md" />
          </div>
        ) : (data?.items?.length ?? 0) === 0 ? (
          <EmptyState
            title="No matching entries"
            description="Try clearing the filters above or pick a wider date range."
            className="border-0"
          />
        ) : (
          <ul className="divide-y divide-border-subtle bg-bg-elevated">
            {data.items.map((entry) => (
              <li key={entry._id}>
                <button
                  type="button"
                  onClick={() => setExpanded(expanded === entry._id ? null : entry._id)}
                  className="flex w-full items-start gap-4 px-4 py-3 text-left hover:bg-bg-elevated-2"
                >
                  <div className="flex w-32 shrink-0 flex-col text-xs text-text-muted">
                    <span>{formatRelativeTime(entry.createdAt)}</span>
                    <span className="text-[10px]">{formatDateTime(entry.createdAt)}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Badge tone={ACTION_TONE[entry.action] ?? "default"}>{entry.action}</Badge>
                      <span className="text-sm text-text-primary">{entry.resource}</span>
                      <span className="text-xs text-text-muted">
                        by{" "}
                        <span className="text-text-secondary">
                          {entry.user?.name ?? entry.user?.email ?? "system"}
                        </span>
                        {entry.user?.role && <span className="ml-1">({entry.user.role})</span>}
                      </span>
                    </div>
                    {entry.meta && Object.keys(entry.meta).length > 0 && (
                      <p className="mt-0.5 truncate font-mono text-xs text-text-muted">
                        {JSON.stringify(entry.meta).slice(0, 120)}
                      </p>
                    )}
                  </div>
                </button>
                {expanded === entry._id && (
                  <pre className="overflow-x-auto border-t border-border-subtle bg-bg-base px-6 py-3 text-xs text-text-secondary">
                    {JSON.stringify(
                      {
                        action: entry.action,
                        resource: entry.resource,
                        resourceId: entry.resourceId,
                        meta: entry.meta,
                        ipAddress: entry.ipAddress,
                        userAgent: entry.userAgent,
                        user: entry.user,
                        createdAt: entry.createdAt,
                      },
                      null,
                      2
                    )}
                  </pre>
                )}
              </li>
            ))}
          </ul>
        )}
        <Pagination meta={data?.meta} onChange={setPage} />
      </div>
    </div>
  );
}
